import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, Loading, Empty, Th, Td, TableWrap, Money, StatusBadge, NativeSelect } from "@/components/kit";
import { dmy } from "@/lib/format";
import { ReceiptScan } from "@/components/ReceiptScan";

export const SIDE_TYPES = {
  sale: ["invoice", "quote", "order", "delivery", "deposit", "fee_note", "credit_note", "debit_note"],
  purchase: ["supplier_invoice", "purchase_order", "receipt", "expense", "supplier_credit", "self_billing"],
};

export default function Documents({ side }) {
  const { api, t, can, download } = useApp();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    setRows(null);
    api.get("/documents", { params: { side, type: type || undefined, status: status || undefined, q: q || undefined } }).then((r) => setRows(r.data));
  }, [api, side, type, status, q]);
  const module = side === "sale" ? "sales" : "purchases";
  return (
    <>
      <PageHeader eyebrow={t(module)} title={t(module)}>
        <Input placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} className="w-48" data-testid="doc-search" />
        <NativeSelect value={type} onChange={(e) => setType(e.target.value)} data-testid="doc-filter-type">
          <option value="">{t("all")}</option>{SIDE_TYPES[side].map((x) => <option key={x} value={x}>{t(`type_${x}`)}</option>)}
        </NativeSelect>
        <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)} data-testid="doc-filter-status">
          <option value="">{t("status")}</option>{["draft", "validated", "sent", "accepted", "rejected", "cancelled", "converted"].map((x) => <option key={x} value={x}>{t(`st_${x}`)}</option>)}
        </NativeSelect>
        <Button variant="outline" onClick={() => download(`/export/documents?side=${side}`, `documents_${side}.xlsx`)} data-testid="doc-export"><Download className="h-4 w-4 me-1" />{t("export_xlsx")}</Button>
        {side === "purchase" && can("purchases", true) && <ReceiptScan variant="outline" />}
        {can(module, true) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button data-testid="doc-new-btn"><Plus className="h-4 w-4 me-1" />{t("new_document")}</Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              {SIDE_TYPES[side].filter((x) => !["credit_note", "supplier_credit"].includes(x)).map((x) => (
                <DropdownMenuItem key={x} onClick={() => nav(`/app/documents/new?type=${x}`)} data-testid={`doc-new-${x}`}>{t(`type_${x}`)}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </PageHeader>
      <Panel>
        {!rows ? <Loading /> : rows.length === 0 ? <Empty /> : (
          <TableWrap>
            <thead className="border-b border-slate-100"><tr><Th>{t("number")}</Th><Th>Type</Th><Th>{t("date")}</Th><Th>{t("partner")}</Th><Th className="text-end">{t("total_ttc")}</Th><Th className="text-end">{t("due")}</Th><Th>{t("status")}</Th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="cursor-pointer border-b border-slate-50 hover:bg-teal-50/40" onClick={() => nav(`/app/documents/${d.id}`)} data-testid={`doc-row-${d.number || d.id}`}>
                  <Td className="num text-xs font-semibold"><Link to={`/app/documents/${d.id}`}>{d.number || "—"}</Link></Td>
                  <Td className="text-xs">{t(`type_${d.type}`)}{d.recurring?.active ? " ↻" : ""}</Td>
                  <Td className="num text-xs">{dmy(d.date)}</Td><Td className="font-medium">{d.partner?.name || "—"}</Td>
                  <Td className="text-end"><Money v={d.totals.ttc * (d.sign || 1)} cur={d.currency} /></Td>
                  <Td className="text-end">{d.due > 0.0005 && d.status !== "draft" && d.sign > 0 ? <Money v={d.due} cur={d.currency} className="text-orange-700" /> : "—"}</Td>
                  <Td><StatusBadge status={d.status} testid={`doc-status-${d.number || d.id}`} /></Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>
    </>
  );
}
